import ApolloClient from 'apollo-client';
import {ApolloLink, Observable} from 'apollo-link';
import {HttpLink} from 'apollo-link-http';
import {InMemoryCache} from 'apollo-cache-inmemory';
import {onError} from 'apollo-link-error';
import SerializingLink from './utils/SerializingLink';
import {setFatalError, setNetworkError} from './app/actions';

// Upper bound on how long we'll wait on a single /api/graphql request.
// Epicor responses can legitimately run 15-30s (sometimes more) when the
// ERP is under load, and nginx's proxy_read_timeout is 240s, so we set
// this high enough that we never abort a request the server is still
// working on.  If you're still seeing timeouts, raise this further - the
// point of the timeout is only to prevent truly wedged requests from
// hanging the UI forever.
const REQUEST_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export default function createApolloClient(store) {
  const client = new ApolloClient({
    link: makeLink(store),
    cache: new InMemoryCache().restore({})
  });

  return client;
}

// Opt-in request timing link.  When enabled, logs every GraphQL operation's
// name + wall-clock duration to the console.  Use this to diagnose whether
// slowness is on the network (long "pending"), the server (long response),
// or the UI (long delay before the request even fires).
//
// Turn on in DevTools console with:
//   localStorage.setItem('bedrock:logGraphql', '1') && location.reload()
// Turn off with:
//   localStorage.removeItem('bedrock:logGraphql') && location.reload()
function shouldLogGraphql() {
  try {
    return (
      typeof window !== 'undefined' &&
      window.localStorage &&
      window.localStorage.getItem('bedrock:logGraphql') === '1'
    );
  } catch (e) {
    return false;
  }
}

function getOperationKind(operation) {
  for (const def of operation.query.definitions) {
    if (def.kind === 'OperationDefinition') return def.operation;
  }
  return 'unknown';
}

const loggingLink = new ApolloLink((operation, forward) => {
  if (!shouldLogGraphql()) return forward(operation);
  const start = Date.now();
  const tag = `${getOperationKind(operation)} ${operation.operationName ||
    '<anon>'}`;
  // eslint-disable-next-line no-console
  console.log(`[graphql] -> ${tag}`);
  return new Observable(observer => {
    const sub = forward(operation).subscribe({
      next: value => observer.next(value),
      error: err => {
        // eslint-disable-next-line no-console
        console.log(
          `[graphql] X  ${tag} FAILED after ${Date.now() - start}ms`,
          err && err.message
        );
        observer.error(err);
      },
      complete: () => {
        // eslint-disable-next-line no-console
        console.log(`[graphql] <- ${tag} ${Date.now() - start}ms`);
        observer.complete();
      }
    });
    return () => sub.unsubscribe();
  });
});

// Wrap window.fetch with an AbortController-based timeout so Apollo
// requests fail after REQUEST_TIMEOUT_MS instead of hanging forever if
// the connection gets truly wedged.  The timeout is intentionally huge -
// Epicor is slow and we'd rather wait than abort a legitimate request.
function makeTimeoutFetch(timeoutMs) {
  return (uri, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(uri, {...options, signal: controller.signal}).finally(() => {
      clearTimeout(timer);
    });
  };
}

function makeLink(store) {
  const http = new HttpLink({
    uri: '/api/graphql',
    credentials: 'same-origin',
    fetch: makeTimeoutFetch(REQUEST_TIMEOUT_MS)
  });
  const errorHandlingLink = onError(({graphQLErrors, networkError}) => {
    if (networkError && networkError.statusCode === 401) {
      window.location.replace('/login');
      return;
    }
    if (networkError) {
      // AbortError (our timeout), TypeError (fetch failed / offline),
      // or a non-2xx response the server never completed.  None of these
      // require a page refresh - tell the user we couldn't reach the
      // server and let them retry.
      const isTimeout =
        networkError.name === 'AbortError' ||
        /aborted|timeout/i.test(networkError.message || '');
      const message = isTimeout
        ? `We couldn't reach the server in time.  Your network or the server may be slow right now.  Please try again.  If this keeps happening, please file a ticket at <a href="https://ithelp.taittowers.com">https://ithelp.taittowers.com</a>.`
        : `We had trouble reaching the server (${networkError.message ||
            'connection error'}).  Please try again.  If this keeps happening, please file a ticket at <a href="https://ithelp.taittowers.com">https://ithelp.taittowers.com</a>.`;
      store.dispatch(setNetworkError(message));
      return;
    }
    if (graphQLErrors && graphQLErrors.length) {
      graphQLErrors.forEach(graphQLError => {
        const errorMessage = graphQLError && graphQLError.message;
        if (errorMessage.startsWith('Duplicate entry')) {
          store.dispatch(
            setFatalError(
              'We have detected duplicate entries. If you have BEDROCK open in another browser tab, or on your mobile device, please close the duplicate session and then click OK. If you continue to see this error, please put a ticket in <a href="https://ithelp.taittowers.com">https://ithelp.taittowers.com</a>'
            )
          );
          return;
        }
      });
    }
  });
  return ApolloLink.from([
    loggingLink,
    errorHandlingLink,
    new SerializingLink(true),
    http
  ]);
}
